import { useState, useMemo, useEffect } from 'react';
import { Typography, Pagination, Box } from '@mui/material';
import { useGetInvoicesListQuery } from '@/store/services/subscriptions';
import { formatCurrency, formatDateWithMoment } from '@/pages/apps/billing/utilities/helpers/helpers';
import DataTable from '@/components/data-table/DataTable';
import { type MRT_ColumnDef } from 'material-react-table';

type InvoiceItem = {
	invoice_id: string;
	invoice_number: string;
	amount_paid: number;
	status: string;
	currency: string;
	invoice_created_date: string;
	invoice_pdf: string;
	plan_name: string;
};

type PaginationType = {
	per_page: number;
	has_more: boolean;
	next_cursor?: string;
	total_returned: number;
};

const PER_PAGE = 10;

function TransactionsTable() {
	const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
	const [currentPage, setCurrentPage] = useState(1);

	const { data: invoiceResponse, isLoading, isFetching } = useGetInvoicesListQuery(
		{
			starting_after: cursorStack[currentPage - 1] ?? undefined,
			per_page: PER_PAGE
		},
		{
			refetchOnMountOrArgChange: true
		}
	);

	const invoices = useMemo(() => {
		return invoiceResponse?.data?.invoices ?? [];
	}, [invoiceResponse?.data?.invoices]);

	const pagination: PaginationType | undefined = invoiceResponse?.data?.pagination;

	useEffect(() => {
		if (!pagination?.next_cursor) return;
		setCursorStack((prev) => {
			const next = [...prev];
			if (next.length <= currentPage) next.length = currentPage + 1;
			next[currentPage] = pagination.next_cursor;
			return next;
		});
	}, [pagination?.next_cursor, currentPage]);

	const pageCount = Math.max(
		currentPage,
		pagination?.has_more ? currentPage + 1 : currentPage
	);

	const handlePageChange = (page: number) => {
		if (page < 1 || page > pageCount) return;
		setCurrentPage(page);
	};

	const showSkeleton = isLoading || isFetching;
	const [tableQuery] = useState('');

	const columns = useMemo<MRT_ColumnDef<InvoiceItem>[]>(
		() => [
			{
				accessorKey: 'invoice_number',
				header: 'Transaction ID',
				enableColumnDragging: false,
				enableColumnActions: false,
				Cell: ({ row }) => (
					<Typography
						variant="body2"
						className="text-gray-900"
					>
						{row.original.invoice_number}
					</Typography>
				)
			},
			{
				accessorKey: 'plan_name',
				header: 'Plan',
				enableColumnDragging: false,
				enableColumnActions: false,
				Cell: ({ row }) => (
					<Typography
						variant="body2"
						className="text-gray-700 capitalize"
					>
						{row.original.plan_name}
					</Typography>
				)
			},
			{
				accessorKey: 'invoice_created_date',
				header: 'Date',
				enableColumnDragging: false,
				enableColumnActions: false,
				Cell: ({ row }) => (
					<Typography
						variant="body2"
						className="text-gray-700"
					>
						{formatDateWithMoment(row.original.invoice_created_date)}
					</Typography>
				)
			},
			{
				accessorKey: 'status',
				header: 'Status',
				enableColumnDragging: false,
				enableColumnActions: false,
				Cell: ({ row }) => (
					<span
						className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold capitalize ${
							row.original.status === 'paid'
								? 'bg-green-100 text-green-800'
								: 'bg-gray-100 text-gray-600'
						}`}
					>
						{row.original.status}
					</span>
				)
			},
			{
				accessorKey: 'amount_paid',
				header: 'Amount',
				enableColumnDragging: false,
				enableColumnActions: false,
				Cell: ({ row }) => (
					<Typography
						variant="body2"
						className="text-gray-700"
					>
						{formatCurrency(row.original.currency || 'usd', row.original.amount_paid || 0)}
					</Typography>
				)
			}
		],
		[]
	);

	return (
		<div>
			{!showSkeleton && invoices.length === 0 ? (
				<div className="bg-white rounded-lg shadow-sm p-12 text-center">
					<Typography
						variant="body2"
						className="text-gray-500 py-8"
					>
						No transactions found.
					</Typography>
				</div>
			) : (
				<div className="bg-white rounded-lg shadow-sm overflow-hidden">
					<div className="overflow-x-auto h-[calc(100vh-212px)]">
						<DataTable
							data={invoices}
							columns={columns}
							query={tableQuery}
							setQuery={() => {}}
							enableTopToolbar={false}
							enableBottomToolbar={false}
							enableRowSelection={false}
							enableRowActions={false}
							enableColumnFilters={false}
							enableSorting={false}
							enableDensityToggle={false}
							enableHiding={false}
							muiTableBodyCellProps={{ sx: { py: 2.3 } }}
							enableToolbarInternalActions={false}
							enableGlobalFilter={false}
							enablePagination={false}
							muiTableHeadRowProps={{ sx: { backgroundColor: '#f9fafb' } }}
							muiTableHeadCellProps={{ sx: { backgroundColor: '#f9fafb' } }}
							muiTableBodyProps={{ sx: { backgroundColor: '#ffffff' } }}
							state={{
								isLoading: showSkeleton,
								showLoadingOverlay: showSkeleton,
								showSkeletons: showSkeleton,
								showProgressBars: showSkeleton
							}}
						/>
					</div>
					{/* Pagination */}
					{pagination && pageCount > 1 && (
						<Box
							className="px-6 py-4 border-t border-gray-200 flex items-center justify-end flex-wrap gap-3"
							sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
						>
							<Pagination
								count={pageCount}
								page={currentPage}
								onChange={(_, value) => handlePageChange(value)}
								color="primary"
								shape="rounded"
								showFirstButton
								showLastButton
								disabled={showSkeleton}
							/>
						</Box>
					)}
				</div>
			)}
		</div>
	);
}

export default TransactionsTable;
